import React from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface Location {
  name: string;
  address: string;
  neighborhood: string;
  phone: string;
  whatsapp: string;
  hours: string[];
  image: string;
  mapLink: string;
}

const ContactInfo: React.FC = () => {
  const { elementRef: refCards, isVisible: visibleCards } = useScrollAnimation({ triggerOnce: true });
  const { elementRef: refMap, isVisible: visibleMap } = useScrollAnimation({ triggerOnce: true });

  const locations: Location[] = [
    {
      name: 'BotoHarmony Parahyba Mall',
      address: 'R. Bacharel José de Oliveira Curchatuz, 850',
      neighborhood: 'Jardim Oceania - João Pessoa, PB',
      phone: '(83) 99861-5180',
      whatsapp: '5583998615180',
      hours: ['Segunda a Sexta: 9h às 19h', 'Sábado: 9h às 14h', 'Domingo: Fechado'],
      image: '/assets/img/logo-01.PNG',
      mapLink: 'https://maps.google.com/?q=R.+Bacharel+José+de+Oliveira+Curchatuz,+850,+João+Pessoa,+PB',
    },
    {
      name: 'BotoHarmony Shopping Tambiá',
      address: 'R. Dep. Odon Bezerra, 184',
      neighborhood: 'Tambiá - João Pessoa, PB',
      phone: '(83) 99311-0469',
      whatsapp: '5583993110469',
      hours: ['Segunda a Sexta: 8h às 18h', 'Sábado: 8h às 13h', 'Domingo: Fechado'],
      image: '/assets/img/logo-01.PNG',
      mapLink: 'https://maps.google.com/?q=R.+Dep.+Odon+Bezerra,+184,+João+Pessoa,+PB',
    },
  ];

  return (
    <section className="py-20 bg-light relative overflow-hidden">
      {/* Decorações sutis */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-[28rem] h-[28rem] bg-primary/10 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        <div className="text-center mb-12">
          <h2 className="section-title">Informações de Contato</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Escolha a unidade mais próxima ou fale conosco diretamente pelos canais abaixo.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cards das unidades */}
          <div
            ref={refCards as React.RefObject<HTMLDivElement>}
            className={`lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-1000 ${
              visibleCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {locations.map((loc, i) => (
              <div
                key={loc.name}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-500"
              >
                {/* Top banner com logo/ornamentos */}
                <div className="relative h-40 bg-gradient-to-br from-secondary/10 to-primary/10 flex items-center justify-center overflow-hidden">
                  <img
                    src={loc.image}
                    alt={loc.name}
                    className="relative z-10 w-28 h-28 object-contain rounded-full bg-white p-3 shadow-md"
                  />
                  <div className="absolute top-6 left-6 w-16 h-16 bg-secondary/20 rounded-full blur-2xl animate-float" />
                  <div className="absolute bottom-6 right-6 w-24 h-24 bg-primary/20 rounded-full blur-2xl animate-float" style={{ animationDelay: `${i * 0.4}s` }} />
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-serif font-bold text-gray-800 mb-3">{loc.name}</h3>

                  <div className="flex items-start gap-3 text-gray-600 mb-3">
                    <svg className="w-5 h-5 text-secondary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p>{loc.address}</p>
                      <p className="text-sm text-gray-500">{loc.neighborhood}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-gray-600 mb-3">
                    <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>{loc.phone}</span>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
                      <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Horário de Funcionamento</span>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600 pl-7">
                      {loc.hours.map((h) => (
                        <p key={h}>{h}</p>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <a
                      href={`https://wa.me/${loc.whatsapp}?text=Olá! Gostaria de agendar uma avaliação na ${encodeURIComponent(loc.name)}.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </a>
                    <a
                      href={loc.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-secondary to-primary hover:from-primary hover:to-secondary text-white px-4 py-2.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      Ver Mapa
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Coluna do mapa/ilustração */}
          <div
            ref={refMap as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-1000 ${visibleMap ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 h-full">
              <div className="aspect-[4/5] md:aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="/assets/img/tambia.jpg"
                  alt="Unidade BotoHarmony"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Canais gerais */}
              <div className="mt-6 text-alt-left md:text-left">
                <h3 className="font-bold text-lg mb-3">Canais Oficiais</h3>
                <div className="grid grid-cols-1 gap-3 text-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="bg-light p-2 rounded-lg">
                      <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.6" />
                        <circle cx="12" cy="11.8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
                        <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
                      </svg>
                    </div>
                    <div>
                      <a
                        href="https://www.instagram.com/botoharmony"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-gray-800 hover:underline"
                      >
                        botoharmony
                      </a>
                      <p className="text-sm text-gray-500">Instagram</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-light p-2 rounded-lg">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p>(83) 99861-5180</p>
                      <p className="text-sm text-gray-500">(83) 99311-0469</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactInfo;